/**
 * Subscription Registration Service
 *
 * @memberof SubscriptionRegistrationComponent
 */

import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {Observable,throwError} from 'rxjs';
import {catchError, map, retry, switchMap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {ParamService} from "./param.service";
import {v4 as uuidv4} from 'uuid';
import { AuthService } from './auth.service';
import {properties} from '../../../environments/properties';
import {SubscriptionService} from './subscription.service';

const storeId = '9999'; // This is the id of the freshpass store, used by the subscription api
const LOW_INCOME_KEY = 'lowincome';

@Injectable()
export class RegistrationSubscriptionService {
    [x: string]: any;

    deliveryStores: any;
    isChaseIntegrationEnabled = true;
    useTokenvaultApi = true;
    banner;
  
    isB2B;
    enableSnap;

    public constructor(private _http: HttpClient,
      private paramService: ParamService,
      private authService: AuthService,
      private subscriptionService: SubscriptionService) {
        this.isB2B = this.authService.checkB2B();
        this.enableSnap = this.isB2B ? false : properties.featureFlags.enableSnap;
        this.banner = this.paramService.banner;
        this.isChaseIntegrationEnabled = window['SWY']['CONFIGSERVICE']?.featureToggleConfig?.disableChaseIntegration ? false : true;
        this.useTokenvaultApi = window['SWY']['CONFIGSERVICE']?.featureToggleConfig?.disableTokenvaultApim ? false : true;
    }

    // --------------------------------------------------------------------------
    /**
     * APIParams() returns XAPI params in object format and add zipcode to object.
     */
    // --------------------------------------------------------------------------
    public APIParams(zipcode: string, banner?: string): object {
        const urlParams = {
            'zipcode': zipcode,
            ...(banner && {'banner': banner})
        };
        return urlParams;
    }

    getEnvironment() {
        let environment;
        if (window.location.origin.indexOf('qa1') > 0 || window.location.origin.indexOf('qa2') > 0 || window.location.origin.indexOf('qa3') > 0 || window.location.origin.indexOf('stage') > 0) {
            environment = window.location.host.split('-')[1].split('.')[0];
        } else if (window.location.origin.indexOf('local') > 0) {
            environment = 'qa1';
        } else {
            environment = 'prod';
        }
        return environment;
    }

    public getApplicationDomainURL() {
        let domain;
        if (window.location.href.indexOf('local') > 0) {
            domain = 'https://www-qa1.' + this.banner + '.com';
        } else {
            domain = 'https://' + window.location.host;
        }
        return domain;
    }

    parseJwt(jwtToken) {
        const base64Url = jwtToken.split('.')[1];
        const base64 = base64Url.replace('-', '+').replace('_', '/');
        return JSON.parse(window.atob(base64));
    }

    getUserClaims(user) {
        const oktaToken = this.parseJwt(user.SWY_SHOP_TOKEN);
        const userClaims = {
            aln: oktaToken['aln'],
            hid: oktaToken['hid'],
            zip: oktaToken['zip'],
            gid: oktaToken['gid'],
            uuid: oktaToken['uuid'],
            exp: oktaToken['exp'],
            firstname: oktaToken['fnm'],
            lastname: oktaToken['lnm']
        };
        return userClaims;
    }

    public replaceHeaderBanner(header) {
        if (header) {
            if (header['banner']) {
                header['banner'] = header['banner'].replace(/\{banner\}/g, this.banner);
            }
            if (header['x-swy_banner']) {
                header['x-swy_banner'] = header['x-swy_banner'].replace(/\{banner\}/g, this.banner);
            }
            if (header['x-swy-banner']) {
                header['x-swy-banner'] = header['x-swy-banner'].replace(/\{banner\}/g, this.banner);
            }
        }
    }

    public getCurrentEnvironment() {
        let environment;
        if (window.location.origin.indexOf('qa1') > 0 || window.location.origin.indexOf('qa2') > 0 || window.location.origin.indexOf('qa3') > 0 || window.location.origin.indexOf('stage') > 0) {
            environment = window.location.host.split('-')[1].split('.')[0];
        } else if (window.location.origin.indexOf('local') > 0) {
            environment = 'qa1';
        } else {
            environment = 'prod';
        }
        return environment;
    }

    // --------------------------------------------------------------------------
    /**
     * Get Stores and update.
     *  /**
     * Call store resolver XAPI zipcodeToShopping to get delivery availability based
     * on zipcode passed.
     * @param urlParams
     */
    // --------------------------------------------------------------------------
    public getShoppingAvailabilityByZipcode(urlParams): Observable<any> {
        const httpOptions = new HttpHeaders().set('Ocp-Apim-Subscription-Key', window['AB']['wcaxXapiApimKey']);
        const url = window['AB']['storeResolverZipcodeToShoppingEndpoint'];

        return this._http.get(url, {headers: httpOptions, params: urlParams})
            .pipe(
                retry(1),
                map((data) => {
                    if (data) {
                        const availability = data['availability'];
                        const deliveryObj = {
                            'available': availability['delivery'].available,
                            'storeId': availability['delivery'].storeId
                        };
                        return deliveryObj;
                    }
                }),
                catchError(this.handleError)
            );
    } // End getShoppingAvailabilityByZipcode

    // --------------------------------------------------------------------------
    /**
     * API call to: /cnc/ucaservice/api/uca/customers/{gid}/preferences/deliverySubscriptions
     */
    // --------------------------------------------------------------------------
    public getCustomerPreferences(): Observable<any> {
        //If valid token makes API call
        if (window['AB']['COMMON'].isSessionTokenValid()) {
            const customerId = window['AB'].userInfo.customerId;
            let currentEnvironment = this.getCurrentEnvironment();
            const url = this.getApplicationDomainURL() + environment[currentEnvironment].dsPreferenceEndPoint.replace('{customerId}', customerId);
            ;

            return this._http.get(url, {headers: window['AB'].ucaHttpOptions})
                .pipe(map((data: any) => {
                        return (data && data.preferences && data.preferences.length > 0) ? {...data.preferences[0]} : [];
                    }),
                    catchError(this.handleError)
                );
        } else {
            //if token expired handle based on KMSI state.
            window['AB']['COMMON'].handleSessionExpiryUser();
        }
    }// getCustomerPreferencesEnd

    // --------------------------------------------------------------------------
    /** Get User Enrollement Details
     * API call to: /abs/acceptancepub/erums/subscriptionservice/api/v1/subscription/user/getUserEnrollmentDetails/{customerId}/{sid}
     * If enrollment call is success Makes post call to ERUMS create subscription.
     */
    // --------------------------------------------------------------------------
    public getUserEnrollmentDetails(sid: string): Observable<any> {
        //If valid token makes API call
        if (window['AB']['COMMON'].isSessionTokenValid()) {
            const customerId = window['AB'].userInfo.customerId;
            let currentEnvironment = this.getCurrentEnvironment();
            const url = this.getApplicationDomainURL() + environment[currentEnvironment].dsJavaBasePath + environment[currentEnvironment].dsJavaUserEnrollmentDetails + '/' + customerId + '/' + sid;
            const headerObj = environment[currentEnvironment].subscriptionHeaders.deliverySubHttpJavaOptions;
            const options = {
              headers: {
                  ...headerObj,
                  'Authorization': `Bearer ${window['AB']?.userInfo.SWY_SHOP_TOKEN}`,
                  'x-swy-correlation-id': uuidv4()
              }
            };
            return this._http.get(url, options)
                .pipe(map((data: any) => {
                        return data;
                    }),
                    catchError(this.handleError)
                );
        } else {
            //if token expired handle based on KMSI state.
            window['AB']['COMMON'].handleSessionExpiryUser();
        }
    }// getUserEnrollmentDetailsEnd

    // --------------------------------------------------------------------------
    /** Get Terms Id
     * API call to:  /api/v1/subscription/user/terms/gid/{gid}
     * If terms call is a success, makes post call to ERUMS create subscription.
     */
    // --------------------------------------------------------------------------
    public getUserTerms(userInfo): Observable<any> {
        //If valid token makes API call
        if (window['AB']['COMMON'].isSessionTokenValid()) {
            const customerId = userInfo.customerId;
            let currentEnvironment = this.getCurrentEnvironment();
            const url = this.getApplicationDomainURL() + environment[currentEnvironment].getTermsInfoApi.url + '/' + customerId;
            let headerObj = environment[currentEnvironment].subscriptionHeaders.deliverySubHttpJavaOptions;
            this.replaceHeaderBanner(headerObj);
            const options = {
              headers: {
                  ...headerObj,
                  'Authorization': `Bearer ${userInfo.SWY_SHOP_TOKEN}`,
                  'x-swy-correlation-id': uuidv4()
              }
            };
            return this._http.get(url, options)
                .pipe(map((data: any) => {
                        return data;
                    }),
                    catchError(this.handleError)
                )
        } else {
            //if token expired handle based on KMSI state.
            window['AB']['COMMON'].handleSessionExpiryUser();
        }
    }// getUserTermsEnd

    // --------------------------------------------------------------------------
    /**
     * API call to: /api/v1/subscription/plans
     */
    // --------------------------------------------------------------------------
    public getPlans(promoDiscountCodeObj = undefined, userInfo): Observable<any> {
        //for guest users make API call

            let currentEnvironment = this.getCurrentEnvironment();
            let url = this.getApplicationDomainURL() + environment[currentEnvironment].dsJavaBasePath + environment[currentEnvironment].dsJavaServiceRestEndpoint;
            
            if (promoDiscountCodeObj && Object.keys(promoDiscountCodeObj).length > 0) {
              let paramKey;
              let paramValue;
              if (promoDiscountCodeObj?.hasOwnProperty(properties.promotionKeys.discountCode)) {
                paramKey = properties.promotionKeys.discountCode;
                paramValue = promoDiscountCodeObj[paramKey];
              } 
              if (promoDiscountCodeObj?.hasOwnProperty(properties.promotionKeys.segment)) {
                paramKey = properties.promotionKeys.segment;
                paramValue = promoDiscountCodeObj[paramKey];
              }
              url += `?${paramKey}=${paramValue}`;
            }

            let headerObj = environment[currentEnvironment].subscriptionHeaders.deliverySubHttpJavaOptions;
            if (this.enableSnap) {
              headerObj = environment[currentEnvironment].subscriptionHeaders.deliverySubHttpJavaOptionsV4
            }
            this.replaceHeaderBanner(headerObj);
            let authToken;
            if (userInfo.SWY_SHOP_TOKEN) {
                authToken = `Bearer ${userInfo.SWY_SHOP_TOKEN}`;
            } else {
                authToken = 'Bearer';
            }
            let currentTime = new Date();

            const options = {
                headers: {
                    ...headerObj,
                    'Authorization': authToken,
                    'x-swy-correlation-id': uuidv4(),
                    'x-swy-date': currentTime.toString()
                }
            };
            return this._http.get(
                url, options)
                .pipe(map((data: any) => {
                        return data;
                    }),
                    catchError(this.handleError)
                );
    }// GetPlansEnd

    // --------------------------------------------------------------------------
    /**
     * Get customer type
     * API call to: /abs/qapub/cnc/ucaservice/api/uca/customers/{customerId}/profile"
     * This is will get customer type, regular customer or employee
     *
     */
    // --------------------------------------------------------------------------
    public getCustomerType(): Observable<any> {
        // for Authorized users to check if they are employees
        const parameters = {'fl': ''};
        const url = window['AB']['COMMON'].ucaAPIPathWithCustomerId(window['AB'].getprofileAddressPath);
        const headers = window['AB'].ucaHttpOptionsGetProfileV2;

        return this._http.get(url, {headers: headers, params: parameters})
            .pipe(map((data: any) => {
                    return data;
                }),
                catchError(this.handleError)
            );
    }

    // --------------------------------------------------------------------------
    /**
     * API call to: /cnc/cwmsservice/api/cwms/customers/{customerId}/tenders
     */
    // --------------------------------------------------------------------------
    public getTenders(): Observable<any> {
        // If valid token makes API call
        if (window['AB']['COMMON'].isSessionTokenValid()) {
            const creditCardInfoPath = this.useTokenvaultApi && this.isChaseIntegrationEnabled ? window['AB'].creditCardInfoTokenvaultPath : window['AB'].creditCardInfoPath;
            const url = creditCardInfoPath.replace('{customerId}', window['AB'].userInfo.customerId);

            let headers = this.isChaseIntegrationEnabled ?
                new HttpHeaders(window['AB'].chasePayeezyHttpOptions).append('storeId', storeId)
                : new HttpHeaders(window['AB'].payeezyHttpOptions);

            if (this.isChaseIntegrationEnabled && this.useTokenvaultApi) {
                headers = headers.set('Ocp-Apim-Subscription-Key', window['AB'].tokenvaultSubscriptionKey);
            }

            return this._http.get(
                url,
                {
                    headers
                }
            ).pipe(map(
                    (data: any) => {
                        return (data && data.tenders && data.tenders.length > 0) ? data['tenders'] : [];
                    }),
                catchError(this.handleError)
            );
        } else {
            // if token expired handle based on KMSI state.
            window['AB']['COMMON'].handleSessionExpiryUser();
        }
    }// getTendersEnd

    // --------------------------------------------------------------------------
    /** Erums create Subscription API to post user selected plan info.
     * API call to: /api/v1/subscription/user/create/{customerId}
     *
     * Displays error msg if API fails
     */
    // --------------------------------------------------------------------------
    public postSubscriptionPlan(userInfo: any, sid: string, requestBody: any, isPrebookReq: string, webSource: any = null): Observable<any> {
        //If valid token makes API call
        if (window['AB']['COMMON'].isSessionTokenValid()) {
            // make enrollment API call to get termsVersionId
            return this.getUserTerms(userInfo).pipe(
                switchMap((data: any) => {
                    // add termsVersionId to post call body.
                    requestBody['termsVersionId'] = data.id;
                    let gid = window['AB'].userInfo.customerId;
                    let currentEnvironment = this.getCurrentEnvironment();
                    let url = this.getApplicationDomainURL() + environment[currentEnvironment].dsJavaBasePath + environment[currentEnvironment].dsJavaSubscriptionCreate.replace('{gid}', gid);
                    const headerObj = environment[currentEnvironment].subscriptionHeaders.deliverySubHttpJavaOptions;
                    const userStoreId = userInfo?.shopStoreId || userInfo?.storeId;
                    const options = {
                      headers: {
                          ...headerObj,
                          'Authorization': `Bearer ${userInfo.SWY_SHOP_TOKEN}`,
                          'x-swy-correlation-id': uuidv4(),
                          'storeid' : userStoreId
                      }
                    };
                    if (isPrebookReq) {
                      options.headers['x-swy-client-name'] = 'WEB-PRE-BOOK';
                    }
                    if (webSource?.param.includes('webSource')) {
                      const urlParams = new URLSearchParams(webSource?.param);
                      const paramValue = urlParams.get('webSource') || '';
                      options.headers['x-swy-client-name'] = paramValue;
                    }

                    return this._http.post(url, JSON.stringify(requestBody), options).pipe(map((response: any) => {
                            return response;
                      }),
                        catchError((error) => { {
                                return this.handleError(error);
                            }
                        })
                    );
                }),
                catchError((error) => {
                  return this.handleError(error);
                })
            );
        } else {
            //if token expired handle based on KMSI state.
            window['AB']['COMMON'].handleSessionExpiryUser();
        }
    }

    // --------------------------------------------------------------------------
    /**
     *  Generate iFrame source based on user action for displaying iFrame
     */
    // --------------------------------------------------------------------------
    public generatePaymentIframe(actionType?: string, tenderId?: string) {
        let currentEnvironment = this.getCurrentEnvironment();
        const oktaToken = window['AB'].userInfo.SWY_SHOP_TOKEN,
            customerId = window['AB'].userInfo.customerId,
            module = 'DELIVERY_SUBSCRIPTION',
            randNum = Math.floor(1000 + Math.random() * 9000),
            baseUrl = this.getApplicationDomainURL() + environment[currentEnvironment].dsPaymentmethodDomain + '/api/v1/payment/',
            banner = window['AB'].userInfo.banner;

        const storeIdParam = this.isChaseIntegrationEnabled ? ('&storeId=' + storeId) : '';
        let url = baseUrl + customerId + randNum + '/' + customerId + '?module=' + module + '&oktaToken=' + oktaToken + '&banner=' + banner + storeIdParam;

        if (actionType === 'update') {
            const dsTenderId = tenderId;
            url = baseUrl + customerId + randNum + '/' + customerId + '/' + dsTenderId + '?module=' + module + '&oktaToken=' + oktaToken + '&banner=' + banner + storeIdParam;
        }
        return url;
    }// generatePaymentIframeEnd

    public getFreshpassTerms(userInfo, currentEnvironment): Observable<any> {
        const url = this.getApplicationDomainURL() + environment[currentEnvironment].getTermsInfoApi.url + '/' + userInfo.customerId
        let headerObj = environment[currentEnvironment].subscriptionHeaders.deliverySubHttpJavaOptions;
        this.replaceHeaderBanner(headerObj);
        const options = {
            headers: {
                ...headerObj,
                'Authorization': `Bearer ${userInfo.SWY_SHOP_TOKEN}`,
                'x-swy-correlation-id': uuidv4()
            }
        };
        return this._http.get(url, options).pipe(
          map((data) => {
            data = this.subscriptionService.replaceBannerPlaceHolderText(data)
            return data;
          })
        );
    }

    public getStores(storeResolverEndpointParam, params: any): Observable<any> {
        let url = storeResolverEndpointParam;
        let currentDomain = this.getApplicationDomainURL()
        const httpOptions = new HttpHeaders().set('Ocp-Apim-Subscription-Key', window['AB'].wcaxXapiApimKey);
        return this._http.get(currentDomain + url, {headers: httpOptions, params: params}).pipe(retry(1));
    }

    public getFreshPassCreateAccountTemplate(currentEnvironment, currentBanner) {
        let url = this.getApplicationDomainURL() + environment[currentEnvironment].createAccountFragment;
        url = url.replace(/\{banner\}/g, currentBanner);
        return this._http.get(url, {responseType: 'text'});

    }

    public getFreshPassSignInTemplate(currentEnvironment) {
        let url = this.getApplicationDomainURL() + environment[currentEnvironment].signInFragement;
        url = url.replace(/\{banner\}/g, this.banner);
        return this._http.get(url, {responseType: 'text'});

    }

    // --------------------------------------------------------------------------
    /**
     * Error Handling for Methods. Returns an error when the call is not passed.
     */
    // --------------------------------------------------------------------------
    private handleError(errorResponse: HttpErrorResponse) {
        if (!(errorResponse.error instanceof ErrorEvent) && errorResponse.error) {
            console.error(`status: ${errorResponse.status}, ${errorResponse.statusText}`);
            return throwError(()=>errorResponse.error);
        }
    }
    
    getAndSetBannerColorText(banner) {
      const BANNER_COLORS = {
        'albertsons': '#00529F',
        'balduccis': '#a9311c',
        'starmarket': '#00529F',
        'kingsfoodmarkets': '#1b6ebb',
        'pavilions': '#675782',
        'shaws': '#4b6f57',
        'default': '#7B1A27'
      };

      let bannerColor = banner ? BANNER_COLORS[banner.toLowerCase()] : BANNER_COLORS.default;

      if (!bannerColor) {
          bannerColor = BANNER_COLORS.default;
      }
      return bannerColor;
    }

}// SubscriptionServiceEnd
